import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import type { EntityAction } from '@/admin/hooks/use-entity-actions'

interface EntityActionExecuteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: EntityAction
  isPending?: boolean
  onConfirm: (values: {
    captured_values: Record<string, unknown>
    reason: string
  }) => void | Promise<void>
}

export function EntityActionExecuteDialog({
  open,
  onOpenChange,
  action,
  isPending = false,
  onConfirm,
}: EntityActionExecuteDialogProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState('')
  const reasonRef = useRef<HTMLTextAreaElement>(null)
  const requireReason = action.confirmation?.require_reason ?? false
  const message =
    action.confirmation?.message || `Are you sure you want to perform "${action.label}"?`

  const handleConfirm = async () => {
    // Confirm stays enabled on an empty reason: the click validates and
    // points the operator at the missing field. A disabled button leaves
    // them hunting for what is wrong with no signal at all.
    if (requireReason && !reason.trim()) {
      setReasonError(t('entityAction.reasonRequired', 'A reason is required'))
      reasonRef.current?.focus()
      return
    }
    setReasonError('')
    await onConfirm({ captured_values: {}, reason })
    setReason('')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setReason('')
          setReasonError('')
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{action.label}</DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.5] text-[var(--ink-2)]">
            {message}
          </DialogDescription>
        </DialogHeader>

        {requireReason && (
          <div className="flex flex-col gap-1.5 px-1">
            <Label
              htmlFor="action-reason"
              className={cn(reasonError && 'text-[var(--status-error)]')}
            >
              Reason
            </Label>
            <Textarea
              id="action-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (reasonError) setReasonError('')
              }}
              placeholder={t(
                'entityAction.reasonPlaceholder',
                'Enter a reason for this action...',
              )}
              rows={3}
              aria-invalid={Boolean(reasonError)}
              aria-describedby={reasonError ? 'action-reason-error' : undefined}
              className={cn(
                reasonError && 'ring-1 ring-[var(--status-error)]',
              )}
            />
            {reasonError && (
              <p
                id="action-reason-error"
                className="text-[12px] text-[var(--status-error)]"
              >
                {reasonError}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant={action.variant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? 'Working...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
